<header class="flex justify-between items-center">
  <h2 mat-dialog-title class="mb0">
    <span>
      @if (toggleHelp) {
        Latex Math Editor
      } @else {
        Equations for Non-LaTeX Users
      }
    </span>
  </h2>
  <div class="flex items-center">
    @if (!toggleHelp) {
      <button mat-icon-button (click)="toggleHelp = !toggleHelp" matTooltip="Hide help">
        <mat-icon>arrow_back</mat-icon>
      </button>
    } @else {
      <button mat-icon-button (click)="toggleHelp = !toggleHelp" matTooltip="Show help">
        <mat-icon>help_outline</mat-icon>
      </button>
    }
  </div>
</header>

<mat-dialog-content [ngClass]="{'hide':!toggleHelp}">
  <h3 for="tex-edit">Code</h3>

  <div class="toolbar flex justify-end">
    @for (toolbarButton of toolbarList; track toolbarButton.label) {
      <button
        class="toolbar-button"
        [disabled]="!(editorHasCursor$ | async)"
        matTooltip="{{ toolbarButton.tooltip }}"
        (mousedown)="$event.preventDefault()"
        (click)="insert(toolbarButton.content)"
      >
        {{ toolbarButton.label }}
      </button>
    }

    <button
      class="toolbar-button"
      matTooltip="Menu for equation examples and environments"
      [matMenuTriggerFor]="examples"
    >
      Examples
    </button>
    <mat-menu #examples="matMenu">
      <button mat-menu-item [matMenuTriggerFor]="equations">Equations</button>
      <button mat-menu-item [matMenuTriggerFor]="environments">Environments</button>
    </mat-menu>
  </div>

  <mat-menu #equations="matMenu">
    @for (equation of equationsList; track equation.label) {
      <button mat-menu-item matTooltip="{{ equation.tooltip }}" (click)="replace(equation.content)">
        {{ equation.label }}
      </button>
    }
  </mat-menu>

  <mat-menu #environments="matMenu">
    <button mat-menu-item (click)="insert('\\begin{align}\n\\end{align}')">align</button>
    <button mat-menu-item (click)="insert('\\begin{gather}\n\\end{gather}')">gather</button>
    <button mat-menu-item (click)="insert('\\begin{equation}\n\\end{equation}')">equation</button>
    <a
      mat-menu-item
      href="https://docs.mathjax.org/en/latest/input/tex/macros/index.html#environments"
      rel="noopener"
      target="_blank"
      >...</a
    >
  </mat-menu>

  <div class="mx0" #editor></div>
  <div class="right-align">
    <small>Using
      <a
        href="https://docs.mathjax.org/en/latest/input/tex/macros/index.html"
        target="_blank"
        rel="noopener"
        >MathJax</a
      >
      to render equation</small>
  </div>

  <div class="mt2">
    <h3 class="m0">Preview</h3>
    @if (results$ | async; as result) {
      <div
        class="p2 preview-container"
        [ngClass]="{'equation__block': !(isInline$ | async), 'equation__inline': (isInline$ | async)}"
      >
        <div #preview [innerHTML]="result?.node"></div>
        @if ((result?.errors?.length ?? 0) > 0) {
          <div class="errors" #errors>
            @for (error of result?.errors; track error.id) {
              <div>
                <b>{{error.id}}</b> {{error.message}}
              </div>
            }
            <div class="help-guideline">
              <button mat-stroked-button color="warn" (click)="copyForAiHelp()" class="copy-ai-btn">
                <mat-icon>smart_toy</mat-icon>
                Copy for AI help
              </button>
              <p>
                Need help with writing math equations? Visit the
                <a
                  href="https://docs.mathjax.org/en/latest/basic/mathematics.html"
                  target="_blank"
                  rel="noopener"
                  >MathJax documentation</a
                >
                for guidance.
              </p>
            </div>
          </div>
        }
      </div>
    }
  </div>

  <div class="my1">
    <h3 class="mb0">Position in text</h3>
    <mat-slide-toggle
      [checked]="isInline$ | async"
      (change)="acceptInlineToggle($event)"
      matTooltip="Inline equations are rendered in the normal text flow like this α while block equations will have space around it and can be numbered as well as cross-referenced."
    >
      inline
    </mat-slide-toggle>
  </div>
</mat-dialog-content>

<mat-dialog-content [ngClass]="{'hide':toggleHelp}" class="eq-help">
  <p class="eq-help__intro">
    SciFlow uses LaTeX syntax for equations, rendered with
    <a href="https://docs.mathjax.org/en/latest/" target="_blank" rel="noopener">MathJax</a>.
    Type LaTeX directly or use a visual editor and paste the result.
  </p>

  <div class="eq-help__features">
    <span class="feature-item"><mat-icon>visibility</mat-icon> Live preview</span>
    <span class="feature-item"><mat-icon>error_outline</mat-icon> Error highlighting</span>
    <span class="feature-item"><mat-icon>swap_vert</mat-icon> Inline &amp; block</span>
  </div>

  <div class="eq-help__editors">
    <h3>Visual equation editors</h3>
    <ul>
      <li>
        <a href="https://latexeditor.lagrida.com/" target="_blank" rel="noopener">Lagrida</a>
        <span class="editor-desc"> — simple, no account</span>
      </li>
      <li>
        <a href="https://www.latex4technics.com/" target="_blank" rel="noopener">LaTeX4technics</a>
        <span class="editor-desc"> — symbols &amp; autocomplete</span>
      </li>
      <li>
        <a href="https://latex.codecogs.com/eqneditor/editor.php" target="_blank" rel="noopener">CodeCogs</a>
        <span class="editor-desc"> — visual builder</span>
      </li>
      <li>
        <a href="https://www.mathcha.io/" target="_blank" rel="noopener">Mathcha</a>
        <span class="editor-desc"> — equations &amp; diagrams</span>
      </li>
    </ul>
  </div>

  <div class="eq-help__footer">
    Any tool that outputs LaTeX math code will work — these are examples, not endorsements.
    Paste the result into the editor.
    <a href="https://docs.mathjax.org/en/latest/input/tex/macros/index.html" target="_blank" rel="noopener">Supported commands</a>
  </div>
</mat-dialog-content>

<mat-dialog-actions align="end" class="mt2">
  @if (toggleHelp) {
    <button mat-button (click)="close()" class="uppercase">
      <span>Cancel</span>
    </button>
    <button mat-button color="primary" (click)="apply()" class="uppercase">Apply changes</button>
  }
</mat-dialog-actions>
